// F-148 client-side line/draft validation. This is feedback only (§1 key decision 2): SQL
// `receiving__draft_analysis` recomputes every issue from persisted rows and the live catalog.

import {
  RECEIVING_LIMITS,
  type ReceivingDraftIssueCode,
  type ReceivingDraftLine,
  type ReceivingDraftLineInput,
  type ReceivingIssue,
  type ReceivingLineIssueCode,
  type ReceivingVariantSnapshot,
} from '../types/stock-receiving';
import { autoAcceptedUnits, quantitiesBalance } from './receiving-line-quantities';

export type ReceivingLineIssue = ReceivingIssue<ReceivingLineIssueCode>;
export type ReceivingDraftIssue = ReceivingIssue<ReceivingDraftIssueCode>;

/** What the validator knows about a line's server-side resolution (optional for unsaved lines). */
export interface ReceivingLineResolution {
  variant: ReceivingVariantSnapshot | null;
  candidates: {
    byBarcode: ReceivingVariantSnapshot[];
    byVariantRef: ReceivingVariantSnapshot | null;
  };
}

function blocking(
  code: ReceivingLineIssueCode,
  lineNo: number,
  field?: string,
  meta?: Record<string, string | number>,
): ReceivingLineIssue {
  const issue: ReceivingLineIssue = { code, severity: 'blocking', lineNo };
  if (field) issue.field = field;
  if (meta) issue.meta = meta;
  return issue;
}

function inRange(value: number | null, min: number, max: number): boolean {
  return value == null || (Number.isInteger(value) && value >= min && value <= max);
}

function resolutionOf(line: ReceivingDraftLineInput | ReceivingDraftLine): ReceivingLineResolution {
  const maybe = line as Partial<ReceivingDraftLine>;
  return {
    variant: maybe.variant ?? null,
    candidates: maybe.candidates ?? { byBarcode: [], byVariantRef: null },
  };
}

function identifierIssues(
  line: ReceivingDraftLineInput,
  lineNo: number,
  resolution: ReceivingLineResolution,
): ReceivingLineIssue[] {
  const { barcode, variantRef } = line.identifiers;
  const eligibleByBarcode = resolution.candidates.byBarcode.filter((c) => c.eligible);
  const byRef = resolution.candidates.byVariantRef;

  if (line.variantId === null) {
    if (barcode === null && variantRef === null) {
      return [blocking('VARIANT_REQUIRED', lineNo, 'variantId')];
    }
    if (eligibleByBarcode.length > 1) {
      return [blocking('IDENTIFIER_AMBIGUOUS', lineNo, 'barcode')];
    }
    const single = eligibleByBarcode.length === 1 ? eligibleByBarcode[0] : undefined;
    if (single) {
      if (byRef && byRef.variantId !== single.variantId) {
        return [blocking('IDENTIFIER_CONFLICT', lineNo, 'variantRef')];
      }
      if (variantRef !== null && byRef === null) {
        return [blocking('IDENTIFIER_UNKNOWN', lineNo, 'variantRef')];
      }
      return [blocking('VARIANT_REQUIRED', lineNo, 'variantId')];
    }
    if (resolution.candidates.byBarcode.length > 0) {
      // barcode matches only deleted/ineligible variants
      return [blocking('VARIANT_INELIGIBLE', lineNo, 'barcode')];
    }
    if (byRef) {
      if (barcode !== null && byRef.barcode !== barcode) {
        return [blocking('IDENTIFIER_CONFLICT', lineNo, 'barcode')];
      }
      return byRef.eligible
        ? [blocking('VARIANT_REQUIRED', lineNo, 'variantId')]
        : [blocking('VARIANT_INELIGIBLE', lineNo, 'variantRef')];
    }
    return [blocking('IDENTIFIER_UNKNOWN', lineNo, barcode !== null ? 'barcode' : 'variantRef')];
  }

  const issues: ReceivingLineIssue[] = [];
  const variant = resolution.variant;
  if (variant && !variant.eligible) {
    issues.push(blocking('VARIANT_INELIGIBLE', lineNo, 'variantId'));
  }
  if (
    variant &&
    line.matchMethod !== 'manual' &&
    barcode !== null &&
    variant.barcode !== barcode &&
    eligibleByBarcode.some((c) => c.variantId !== variant.variantId)
  ) {
    issues.push(blocking('IDENTIFIER_CONFLICT', lineNo, 'barcode'));
  }
  return issues;
}

function quantityIssues(
  line: ReceivingDraftLineInput,
  lineNo: number,
  variant: ReceivingVariantSnapshot | null,
): ReceivingLineIssue[] {
  const issues: ReceivingLineIssue[] = [];
  const max = RECEIVING_LIMITS.maxQuantity;

  if (line.unitKind === 'pack') {
    if (line.packsExpected == null)
      issues.push(blocking('QUANTITY_MISSING', lineNo, 'packsExpected'));
    if (line.unitsPerPack == null)
      issues.push(blocking('QUANTITY_MISSING', lineNo, 'unitsPerPack'));
    if (!inRange(line.packsExpected, 1, RECEIVING_LIMITS.maxPacks)) {
      issues.push(blocking('QUANTITY_OUT_OF_RANGE', lineNo, 'packsExpected'));
    }
    if (!inRange(line.unitsPerPack, 1, RECEIVING_LIMITS.maxUnitsPerPack)) {
      issues.push(blocking('QUANTITY_OUT_OF_RANGE', lineNo, 'unitsPerPack'));
    }
    if (line.packsExpected != null && line.unitsPerPack != null) {
      if (line.unitsPerPackConfirmed !== line.unitsPerPack) {
        issues.push(blocking('PACK_CONFIRMATION_REQUIRED', lineNo, 'unitsPerPackConfirmed'));
      }
      const product = line.packsExpected * line.unitsPerPack;
      if (product > max) {
        issues.push(blocking('QUANTITY_OUT_OF_RANGE', lineNo, 'expectedUnits'));
      } else if (line.expectedUnits !== product) {
        issues.push(blocking('PACK_TOTAL_MISMATCH', lineNo, 'expectedUnits', { product }));
      }
    }
  }

  if (line.expectedUnits == null) {
    issues.push(blocking('QUANTITY_MISSING', lineNo, 'expectedUnits'));
  } else if (!inRange(line.expectedUnits, 1, max)) {
    issues.push(blocking('QUANTITY_OUT_OF_RANGE', lineNo, 'expectedUnits'));
  }
  for (const field of ['acceptedUnits', 'damagedUnits', 'missingUnits'] as const) {
    const value = line[field];
    if (value == null) {
      issues.push(blocking('QUANTITY_MISSING', lineNo, field));
    } else if (!inRange(value, 0, max)) {
      issues.push(blocking('QUANTITY_OUT_OF_RANGE', lineNo, field));
    }
  }

  const allPresent =
    line.expectedUnits != null &&
    line.acceptedUnits != null &&
    line.damagedUnits != null &&
    line.missingUnits != null;
  if (allPresent && !quantitiesBalance(line)) {
    issues.push(
      blocking('QUANTITY_SUM_MISMATCH', lineNo, 'acceptedUnits', {
        expected: line.expectedUnits ?? 0,
        calculated: autoAcceptedUnits(line) ?? 0,
      }),
    );
  }

  if (variant && line.acceptedUnits != null && variant.inventory + line.acceptedUnits > max) {
    issues.push(
      blocking('INVENTORY_LIMIT_EXCEEDED', lineNo, 'acceptedUnits', {
        inventoryNow: variant.inventory,
      }),
    );
  }
  return issues;
}

/**
 * Validates one line. `lineNo` is 1-based (position in the draft). Resolution comes from the
 * line itself when it is a `ReceivingDraftLine`, or from the optional third argument.
 */
export function validateDraftLine(
  line: ReceivingDraftLineInput | ReceivingDraftLine,
  lineNo: number,
  resolution?: ReceivingLineResolution,
): ReceivingLineIssue[] {
  const res = resolution ?? resolutionOf(line);
  return [...identifierIssues(line, lineNo, res), ...quantityIssues(line, lineNo, res.variant)];
}

/** AC12: variantId → 1-based line numbers, only for variants that appear on ≥ 2 lines. */
export function findDuplicateVariants(
  lines: ReadonlyArray<Pick<ReceivingDraftLineInput, 'variantId'>>,
): Map<string, number[]> {
  const byVariant = new Map<string, number[]>();
  lines.forEach((line, index) => {
    if (line.variantId === null) return;
    const list = byVariant.get(line.variantId);
    if (list) list.push(index + 1);
    else byVariant.set(line.variantId, [index + 1]);
  });
  for (const [variantId, lineNos] of byVariant) {
    if (lineNos.length < 2) byVariant.delete(variantId);
  }
  return byVariant;
}

/** Draft-level issues (client feedback): NO_LINES, TOO_MANY_LINES and DUPLICATE_VARIANT per line. */
export function validateDraftLines(
  lines: ReadonlyArray<ReceivingDraftLineInput | ReceivingDraftLine>,
): { draftIssues: ReceivingDraftIssue[]; lineIssues: ReceivingLineIssue[] } {
  const draftIssues: ReceivingDraftIssue[] = [];
  if (lines.length === 0) draftIssues.push({ code: 'NO_LINES', severity: 'blocking' });
  if (lines.length > RECEIVING_LIMITS.maxLines) {
    draftIssues.push({
      code: 'TOO_MANY_LINES',
      severity: 'blocking',
      meta: { max: RECEIVING_LIMITS.maxLines, count: lines.length },
    });
  }
  const lineIssues: ReceivingLineIssue[] = [];
  lines.forEach((line, index) => lineIssues.push(...validateDraftLine(line, index + 1)));
  for (const [variantId, lineNos] of findDuplicateVariants(lines)) {
    for (const lineNo of lineNos) {
      lineIssues.push(
        blocking('DUPLICATE_VARIANT', lineNo, 'variantId', {
          variantId,
          lineNos: lineNos.join(','),
        }),
      );
    }
  }
  return { draftIssues, lineIssues };
}

function sumNullable(values: Array<number | null>): number | null {
  if (values.every((v) => v == null)) return null;
  return values.reduce<number>((acc, v) => acc + (v ?? 0), 0);
}

/**
 * AC12 "Combine rows" (§4.8): merges every line with `variantId` into the first such line.
 * Quantities are summed field by field; `acceptedAuto` is true only if every source line is
 * true, and then accepted = autoAcceptedUnits. Unit kind: all unit → unit; all pack with the
 * same units-per-pack → pack (packs summed, confirmation kept only if every line confirmed);
 * otherwise the result becomes a unit line (pack conversion is never inferred).
 */
export function consolidateLines<L extends ReceivingDraftLineInput>(
  lines: readonly L[],
  variantId: string,
): { lines: L[]; combined: L | null; removedClientLineIds: string[] } {
  const sources = lines.filter((l) => l.variantId === variantId);
  const first = sources[0];
  if (sources.length < 2 || first === undefined) {
    return { lines: [...lines], combined: null, removedClientLineIds: [] };
  }
  const acceptedAuto = sources.every((l) => l.acceptedAuto);
  const allUnit = sources.every((l) => l.unitKind === 'unit');
  const samePack =
    !allUnit &&
    sources.every((l) => l.unitKind === 'pack' && l.unitsPerPack === first.unitsPerPack);

  let combined: L = {
    ...first,
    expectedUnits: sumNullable(sources.map((l) => l.expectedUnits)),
    acceptedUnits: sumNullable(sources.map((l) => l.acceptedUnits)),
    damagedUnits: sumNullable(sources.map((l) => l.damagedUnits)),
    missingUnits: sumNullable(sources.map((l) => l.missingUnits)),
    acceptedAuto,
    note:
      sources
        .map((l) => l.note)
        .filter((n): n is string => n !== null)
        .join(' | ') || null,
  };
  if (samePack) {
    combined = {
      ...combined,
      unitKind: 'pack',
      packsExpected: sumNullable(sources.map((l) => l.packsExpected)),
      unitsPerPackConfirmed: sources.every((l) => l.unitsPerPackConfirmed === l.unitsPerPack)
        ? first.unitsPerPack
        : null,
    };
  } else {
    combined = {
      ...combined,
      unitKind: 'unit',
      packsExpected: null,
      unitsPerPack: null,
      unitsPerPackConfirmed: null,
    };
  }
  if (acceptedAuto) {
    combined = { ...combined, acceptedUnits: autoAcceptedUnits(combined) };
  }

  const removed = sources.slice(1).map((l) => l.clientLineId);
  const result = lines
    .filter((l) => l.variantId !== variantId || l.clientLineId === first.clientLineId)
    .map((l) => (l.clientLineId === first.clientLineId ? combined : l));
  return { lines: result, combined, removedClientLineIds: removed };
}
